import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card, Typography, Table, Switch, Button, Space, useApp, Alert, Tag, TimePicker, Row, Col, Divider,
  Popconfirm, Tooltip, theme,
} from '../../ui';
import {
  Bell, Save, RefreshCw, Monitor, Smartphone, Trash2,
} from 'lucide-react';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import {
  enableWebPush, disableWebPush, isPushSupportedHere, isFcmConfigured, currentPermission,
} from '../../lib/fcm';
import { formatIst } from '../../lib/time';

type Pref = {
  eventCode: string;
  label: string;
  group: string;
  fcm: boolean;
  whatsapp: boolean;
  sms: boolean;
  email: boolean;
  quietHoursStart?: string | null;
  quietHoursEnd?: string | null;
};

export function NotificationPrefsPage() {
  const { token } = theme.useToken();
  const { message } = useApp();
  const qc = useQueryClient();
  const [prefs, setPrefs] = useState<Pref[]>([]);
  const [quietStart, setQuietStart] = useState<dayjs.Dayjs | null>(null);
  const [quietEnd, setQuietEnd] = useState<dayjs.Dayjs | null>(null);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(currentPermission());

  const { data, isLoading } = useQuery({
    queryKey: ['notif-prefs'],
    queryFn: () => request<Pref[]>({ url: '/notifications/preferences' }),
  });

  const { data: devices } = useQuery({
    queryKey: ['notif-devices'],
    queryFn: () => request<any[]>({ url: '/notifications/devices' }),
  });

  useEffect(() => {
    if (data) {
      setPrefs(data);
      const first = data[0];
      setQuietStart(first?.quietHoursStart ? dayjs(first.quietHoursStart, 'HH:mm:ss') : null);
      setQuietEnd(first?.quietHoursEnd ? dayjs(first.quietHoursEnd, 'HH:mm:ss') : null);
    }
  }, [data]);

  useEffect(() => {
    isPushSupportedHere().then(setPushSupported);
  }, []);

  const save = useMutation({
    mutationFn: () => request({
      url: '/notifications/preferences',
      method: 'PUT',
      data: {
        preferences: prefs.map((p) => ({
          eventCode: p.eventCode,
          fcm: p.fcm,
          whatsapp: p.whatsapp,
          sms: p.sms,
          email: p.email,
        })),
        quietHoursStart: quietStart ? quietStart.format('HH:mm:ss') : null,
        quietHoursEnd: quietEnd ? quietEnd.format('HH:mm:ss') : null,
      },
    }),
    onSuccess: () => {
      message.success('Preferences saved');
      qc.invalidateQueries({ queryKey: ['notif-prefs'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save'),
  });

  const revokeDevice = useMutation({
    mutationFn: (id: string) => request({ url: `/notifications/devices/${id}`, method: 'DELETE' }),
    onSuccess: () => {
      message.success('Device removed');
      qc.invalidateQueries({ queryKey: ['notif-devices'] });
    },
  });

  const flip = (eventCode: string, channel: 'fcm' | 'whatsapp' | 'sms' | 'email', value: boolean) => {
    setPrefs((rows) => rows.map((r) => (r.eventCode === eventCode ? { ...r, [channel]: value } : r)));
  };

  const setAll = (channel: 'fcm' | 'whatsapp' | 'sms' | 'email', value: boolean) => {
    setPrefs((rows) => rows.map((r) => ({ ...r, [channel]: value })));
  };

  const groups = useMemo(() => {
    const map: Record<string, Pref[]> = {};
    for (const p of prefs) (map[p.group] ??= []).push(p);
    return Object.entries(map);
  }, [prefs]);

  const onEnablePush = async () => {
    setPushBusy(true);
    try {
      await enableWebPush();
      setPermission('granted');
      qc.invalidateQueries({ queryKey: ['notif-devices'] });
      message.success('Web push enabled on this device');
    } catch (e: any) {
      message.error(e.message ?? 'Could not enable web push');
    } finally {
      setPushBusy(false);
    }
  };

  const onDisablePush = async () => {
    setPushBusy(true);
    try {
      await disableWebPush();
      qc.invalidateQueries({ queryKey: ['notif-devices'] });
      message.success('Web push disabled on this device');
    } catch (e: any) {
      message.error(e.message ?? 'Could not disable web push');
    } finally {
      setPushBusy(false);
    }
  };

  const columns = [
    {
      title: 'Event',
      dataIndex: 'label',
      render: (v: string, r: Pref) => (
        <div>
          <div style={{ fontWeight: 500 }}>{v}</div>
          <code style={{ fontSize: 11, color: token.colorTextTertiary }}>{r.eventCode}</code>
        </div>
      ),
    },
    {
      title: 'Push',
      dataIndex: 'fcm',
      width: 90,
      align: 'center' as const,
      render: (v: boolean, r: Pref) => <Switch checked={v} onCheckedChange={(c) => flip(r.eventCode, 'fcm', c)} />,
    },
    {
      title: 'WhatsApp',
      dataIndex: 'whatsapp',
      width: 100,
      align: 'center' as const,
      render: (v: boolean, r: Pref) => <Switch checked={v} onCheckedChange={(c) => flip(r.eventCode, 'whatsapp', c)} />,
    },
    {
      title: 'SMS',
      dataIndex: 'sms',
      width: 80,
      align: 'center' as const,
      render: (v: boolean, r: Pref) => <Switch checked={v} onCheckedChange={(c) => flip(r.eventCode, 'sms', c)} />,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      width: 80,
      align: 'center' as const,
      render: (v: boolean, r: Pref) => <Switch checked={v} onCheckedChange={(c) => flip(r.eventCode, 'email', c)} />,
    },
  ];

  const pushStatusTag = (() => {
    if (!pushSupported) return <Tag tone="neutral">Not supported in this browser</Tag>;
    if (!isFcmConfigured()) return <Tag tone="gold">Firebase not configured</Tag>;
    if (permission === 'granted') return <Tag tone="green">Enabled</Tag>;
    if (permission === 'denied') return <Tag tone="red">Blocked in browser settings</Tag>;
    return <Tag tone="neutral">Off</Tag>;
  })();

  return (
    <div>
      <Typography.Title level={4}>Notification Preferences</Typography.Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Decide which channel each event should reach you on."
        description="Turning a channel off here means we won't try to send that event through that channel. WhatsApp templates still respect Meta's policy windows."
      />

      <Row gutter={16}>
        <Col xs={24} md={14}>
          <Card
            title={<><Bell size={16} /> Per-event routing</>}
            extra={
              <Space>
                <Tooltip title="Reset un-saved changes">
                  <Button icon={<RefreshCw size={16} />} onClick={() => data && setPrefs(data)}>Reset</Button>
                </Tooltip>
                <Button variant="primary" icon={<Save size={16} />} loading={save.isPending} onClick={() => save.mutate()}>
                  Save changes
                </Button>
              </Space>
            }
            loading={isLoading}
          >
            <Space style={{ marginBottom: 8 }} wrap>
              <Typography.Text type="secondary">Bulk:</Typography.Text>
              <Button size="sm" onClick={() => setAll('fcm', true)}>All Push on</Button>
              <Button size="sm" onClick={() => setAll('fcm', false)}>All Push off</Button>
              <Button size="sm" onClick={() => setAll('whatsapp', true)}>All WhatsApp on</Button>
              <Button size="sm" onClick={() => setAll('whatsapp', false)}>All WhatsApp off</Button>
            </Space>
            {groups.map(([g, rows]) => (
              <div key={g} style={{ marginBottom: 16 }}>
                <Typography.Title level={5} style={{ margin: '8px 0' }}>{g}</Typography.Title>
                <Table
                  size="small"
                  rowKey="eventCode"
                  dataSource={rows}
                  columns={columns}
                  pagination={false}
                />
              </div>
            ))}
          </Card>
        </Col>

        <Col xs={24} md={10}>
          <Card title="Web push on this browser" extra={pushStatusTag}>
            {!isFcmConfigured() && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="Firebase web config missing"
                description="Set VITE_FIREBASE_* and VITE_FIREBASE_VAPID_KEY in frontend env, then rebuild."
              />
            )}
            {pushSupported && isFcmConfigured() && permission !== 'granted' && (
              <Button variant="primary" loading={pushBusy} icon={<Bell size={16} />} onClick={onEnablePush}>
                Enable notifications on this device
              </Button>
            )}
            {pushSupported && isFcmConfigured() && permission === 'granted' && (
              <Button loading={pushBusy} onClick={onDisablePush}>
                Disable push on this device
              </Button>
            )}
            <Divider />
            <Typography.Title level={5}>My registered devices</Typography.Title>
            {(devices ?? []).length === 0 && <Typography.Text type="secondary">No devices registered yet.</Typography.Text>}
            {(devices ?? []).map((d: any) => (
              <Card key={d.id} style={{ marginBottom: 6 }}>
                <Row align="middle">
                  <Col flex="auto">
                    <Space>
                      {d.platform === 'web' ? <Monitor size={16} /> : <Smartphone size={16} />}
                      <span style={{ fontWeight: 500 }}>{d.deviceModel ?? d.platform}</span>
                      <Tag>{d.platform}</Tag>
                    </Space>
                    <div style={{ fontSize: 12, color: token.colorTextSecondary, marginTop: 2 }}>
                      Last seen {formatIst(d.lastSeenAt)}
                    </div>
                  </Col>
                  <Col>
                    <Popconfirm title="Remove this device?" onConfirm={() => revokeDevice.mutate(d.id)}>
                      <Button size="sm" variant="danger" icon={<Trash2 size={16} />} aria-label="Remove device" />
                    </Popconfirm>
                  </Col>
                </Row>
              </Card>
            ))}

            <Divider />
            <Typography.Title level={5}>Quiet hours</Typography.Title>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
              Push and SMS won't fire during this window — anything queued is delivered when the window ends. Auth OTPs always fire.
            </Typography.Paragraph>
            <Space>
              <TimePicker value={quietStart} onChange={setQuietStart} format="hh:mm A" use12Hours minuteStep={15} placeholder="From" />
              <TimePicker value={quietEnd} onChange={setQuietEnd} format="hh:mm A" use12Hours minuteStep={15} placeholder="To" />
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
